import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";

export default function MarketVolatilityModal({ onClose }) {
  const [pressures, setPressures] = useState(null);
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  const modalRef = useRef(null);
  const [drag, setDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const fetchPressures = async () => {
      try {
        const docRef = doc(db, "Global_Vars", "Main");
        const snap = await getDoc(docRef);
        if (snap.exists()) setPressures(snap.data());
      } catch (err) {
        console.error("Error fetching pressures:", err);
      }
    };
    fetchPressures();
  }, []);

  const pressureData = useMemo(() => {
    if (!pressures) return [];
    const keys = [
      { key: "regionPressureAF", name: "Africa" },
      { key: "regionPressureAS", name: "Asia" },
      { key: "regionPressureAU", name: "Australia" },
      { key: "regionPressureEU", name: "Europe" },
      { key: "regionPressureNA", name: "North America" },
      { key: "regionPressureSA", name: "South America" },
      { key: "regionPressurefalse", name: "Global" },
    ];

    const values = keys.map(k => ({
      name: k.name,
      value: pressures[k.key] ?? 0,
    }));

    const total = values.reduce((sum, v) => sum + v.value, 0);
    const avg = total / values.length;

    return values.map(v => ({
      ...v,
      avg,
      percentage: avg ? ((v.value / avg) * 100).toFixed(2) : 0,
    }));
  }, [pressures]);

  const globalTaxRate = pressures?.globalBaseTaxRate ?? 0;
  
  // --- COMMUNITY EVENT TRACKING METRICS ---
  const globalGoldMultiplier = pressures?.globalGoldMultiplier ?? 1;
  const buffThreshold = pressures?.globalGoldBuffThreshold ?? 0;
  const buffDonations = pressures?.globalGoldBuffDonations ?? 0;
  const amountRemainingToBuff = Math.max(0, buffThreshold - buffDonations);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = modal;
    setPosition({
      top: innerHeight / 2 - offsetHeight / 2,
      left: innerWidth / 2 - offsetWidth / 2,
    });
  }, []);

  const handleMouseDown = (e) => {
    const modal = modalRef.current;
    if (!modal) return;

    setZIndex(getNextZ() + 10000);

    const rect = modal.getBoundingClientRect();
    setDrag({
      isDragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!drag.isDragging) return;
    setPosition({
      top: e.clientY - drag.offsetY,
      left: e.clientX - drag.offsetX,
    });
  };

  const handleMouseUp = () => {
    if (drag.isDragging) setDrag(prev => ({ ...prev, isDragging: false }));
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  const modalContent = (
    <div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: "320px",
        zIndex,
        transform: "none",
        background: "#080d0d",
        border: "1px solid rgba(80, 227, 194, 0.2)",
        fontFamily: "monospace"
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="draggable-title" style={{ padding: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(80, 227, 194, 0.2)" }}>
        <span style={{ color: "#50E3C2", fontWeight: "bold", fontSize: "0.8rem", letterSpacing: "1px" }}>MARKET STATUS</span>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#50E3C2", cursor: "pointer", fontSize: "1rem" }}>×</button>
      </div>
      
      <div className="scrollable-content" style={{ padding: "10px", display: "flex", flexDirection: "column", gap: "12px", maxHeight: "400px", overflowY: "auto" }}>
        {!pressureData.length ? (
          <p className="blink" style={{ color: "#50E3C2", textAlign: "center", fontSize: "0.8rem" }}>LOADING...</p>
        ) : (
          <>
            {/* BOX 1: ECONOMY & GLOBAL MULTIPLIERS */}
            <div style={{ background: "rgba(80, 227, 194, 0.03)", border: "1px solid rgba(80, 227, 194, 0.15)", borderRadius: "4px", padding: "8px" }}>
              <div style={{ color: "#50E3C2", fontSize: "0.65rem", fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px", borderBottom: "1px solid rgba(80, 227, 194, 0.15)", paddingBottom: "3px" }}>
                GLOBAL VARIABLES
              </div>
              
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", padding: "3px 0", color: "rgba(255,255,255,0.7)" }}>
                <span>GLOBAL TAX RATE:</span>
                <span style={{ color: "#50E3C2", fontWeight: "bold" }}>{globalTaxRate}%</span>
              </div>
              
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", padding: "3px 0", color: "rgba(255,255,255,0.7)" }}>
                <span>MINT MULTIPLIER:</span>
                <span style={{ color: "#ffaa00", fontWeight: "bold" }}>{globalGoldMultiplier.toFixed(1)}x</span>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.65rem", padding: "3px 0", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>
                <span>BUFF STATUS:</span>
                <span style={{ 
                  color: amountRemainingToBuff > 0 ? "#fff" : "#ffaa00", 
                  fontWeight: "bold",
                  background: amountRemainingToBuff > 0 ? "rgba(255,255,255,0.05)" : "rgba(255,170,0,0.1)",
                  padding: "1px 4px",
                  borderRadius: "2px",
                  fontSize: "0.6rem"
                }}>
                  {amountRemainingToBuff > 0 ? `$${amountRemainingToBuff.toLocaleString()} REMAINING` : "MAX MULTIPLIER ACTIVE"}
                </span>
              </div>
            </div>

            {/* BOX 2: REGIONAL VOLATILITY INDEX */}
            <div style={{ background: "rgba(255, 255, 255, 0.01)", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: "4px", padding: "8px" }}>
              <div style={{ color: "#fff", opacity: 0.6, fontSize: "0.65rem", fontWeight: "bold", letterSpacing: "1px", marginBottom: "6px", borderBottom: "1px solid rgba(255, 255, 255, 0.08)", paddingBottom: "3px" }}>
                REGIONAL MARKET FORCE INDEX
              </div>
              
              <table className="currency-exchange-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", fontSize: "0.6rem", opacity: 0.4 }}>
                    <th style={{ paddingBottom: "4px", fontWeight: "normal" }}>REGION</th>
                    <th style={{ paddingBottom: "4px", fontWeight: "normal", textAlign: "right" }}>PRESSURE</th>
                  </tr>
                </thead>
                <tbody>
                  {pressureData.map((p, idx) => {
                    const deviation = p.percentage - 100;
                    const clamped = Math.max(-100, Math.min(100, deviation));
                    const gradient =
                      clamped < 0
                        ? "linear-gradient(to top, #2b6cb0, #718096)"
                        : clamped > 0
                        ? "linear-gradient(to top, #718096, #e53e3e)"
                        : "linear-gradient(to top, #718096, #718096)";

                    return (
                      <tr key={idx} style={{ fontSize: "0.7rem", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                        <td style={{ color: "rgba(255,255,255,0.85)", padding: "4px 0" }}>{p.name}</td>
                        <td
                          style={{
                            fontWeight: "bold",
                            textAlign: "right",
                            padding: "4px 0",
                            background: gradient,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          {p.percentage}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}